import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Markdown } from "@/components/Markdown";
import { ShareButtons } from "@/components/ShareButtons";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function NewsArticle() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/news/${slug}`).then((r) => setItem(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;
  if (notFound || !item) return (
    <div className="py-32 text-center">
      <p className="text-brand-muted">This article could not be found.</p>
      <Link to="/news" className="mt-4 inline-block text-brand-terracotta underline">Back to News</Link>
    </div>
  );

  return (
    <div data-testid="news-article-page">
      <Seo title={item.title} path={`/news/${item.slug}`} description={item.excerpt} image={item.cover_image} />
      <article className="mx-auto max-w-3xl px-6 py-14">
        <Link to="/news" data-testid="back-to-news" className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to News
        </Link>
        <p className="text-xs text-brand-muted uppercase tracking-wider">{fmtDate(item.created_at)}</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-brand leading-[1.1]">{item.title}</h1>
        <div className="mt-5"><ShareButtons title={item.title} /></div>
        {item.cover_image && (
          <img src={item.cover_image} alt={item.title} className="mt-8 rounded-sm w-full h-[420px] object-cover" />
        )}
        <div className="mt-10">
          <Markdown>{item.body}</Markdown>
        </div>

        {item.gallery?.length > 0 && (
          <div className="mt-10" data-testid="news-gallery">
            <h2 className="font-display text-xl text-brand mb-4">Gallery</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {item.gallery.map((src, i) => (
                <a key={src} href={src} target="_blank" rel="noreferrer noopener" className="block rounded-sm overflow-hidden bg-brand-surface">
                  <img src={src} alt={`${item.title} photo ${i + 1}`} className="w-full h-56 object-cover hover:scale-105 transition-transform duration-500" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 pt-8 border-t border-brand/10 flex items-center justify-between gap-4 flex-wrap">
          <ShareButtons title={item.title} />
          <Link to="/news" className="text-sm text-brand-terracotta underline underline-offset-2">← All news</Link>
        </div>
      </article>
    </div>
  );
}
